import { ref } from 'vue';
import { isNil } from 'lodash-es';
import { defineStore } from 'pinia';
import { DB_TYPE } from '@/common/define/common.define';
import { type DBType } from '@/common/utils/types';
import type { PaDbType, InstanceInfo } from '@/database/utils/pa/pa.types';
import { useDatabaseMetaStore } from './database-meta';
import { nodeIdFilterCallback } from '../utils/utils';

type PaDbSourceMap<SourceType> = Record<PaDbType, SourceType>;

interface StoredInstanceData {
  instanceId: string | null;
  groupId?: string | null;
  instanceName?: string;
}

interface StoredTimeData {
  fromTime?: string;
  toTime?: string;
  fromUtc0Time?: string;
  toUtc0Time?: string;
}

export type StoredData = StoredInstanceData & StoredTimeData;

const INITIAL_INSTANCE_DATA: StoredInstanceData = {
  groupId: null,
  instanceId: null,
  instanceName: '',
};

const INITIAL_TIME_DATA: StoredTimeData = {
  fromTime: '',
  toTime: '',
  fromUtc0Time: '',
  toUtc0Time: '',
};

const getInitialSourceMap = <SourceType>(initialValue: SourceType) =>
  Object.fromEntries(
    Object.values(DB_TYPE).map((dbType) => [dbType, initialValue]),
  ) as PaDbSourceMap<SourceType>;

const useDatabasePaInstanceCondition = () => {
  const selectedInstanceData = ref<PaDbSourceMap<StoredInstanceData>>(
    getInitialSourceMap<StoredInstanceData>({ ...INITIAL_INSTANCE_DATA }),
  );

  const getSelectedGroupId = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedInstanceData.value[dbType]?.groupId ?? '');
  const getSelectedInstanceId = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedInstanceData.value[dbType]?.instanceId ?? '');
  const getSelectedInstanceName = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedInstanceData.value[dbType]?.instanceName ?? '');

  const setSelectedGroupId = (dbType: DBType, groupId: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedInstanceData.value[dbType] = { ...selectedInstanceData.value[dbType], groupId };
  };

  const setSelectedInstanceId = (dbType: DBType, instanceId: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedInstanceData.value[dbType] = { ...selectedInstanceData.value[dbType], instanceId };
  };

  const setSelectedInstanceName = (dbType: DBType, instanceName?: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedInstanceData.value[dbType] = { ...selectedInstanceData.value[dbType], instanceName };
  };

  const initSelectedInstance = (dbType: DBType) => {
    if (isNil(dbType)) {
      return;
    }

    selectedInstanceData.value[dbType] = { ...INITIAL_INSTANCE_DATA };
  };

  const initAllSelectedInstance = () => {
    selectedInstanceData.value = getInitialSourceMap<StoredInstanceData>({
      ...INITIAL_INSTANCE_DATA,
    });
  };

  return {
    getSelectedGroupId,
    getSelectedInstanceId,
    getSelectedInstanceName,

    setSelectedGroupId,
    setSelectedInstanceId,
    setSelectedInstanceName,

    initSelectedInstance,
    initAllSelectedInstance,
  };
};

export const useDatabasePaTimeCondition = () => {
  const selectedTimeData = ref<PaDbSourceMap<StoredTimeData>>(
    getInitialSourceMap<StoredTimeData>({ ...INITIAL_TIME_DATA }),
  );

  const getSelectedFromTime = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedTimeData.value[dbType]?.fromTime ?? '');
  const getSelectedToTime = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedTimeData.value[dbType]?.toTime ?? '');
  const getSelectedFromUtc0Time = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedTimeData.value[dbType]?.fromUtc0Time ?? '');
  const getSelectedToUtc0Time = (dbType: DBType): string =>
    isNil(dbType) ? '' : (selectedTimeData.value[dbType]?.toUtc0Time ?? '');

  const setSelectedFromTime = (dbType: DBType, date?: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedTimeData.value[dbType] = { ...selectedTimeData.value[dbType], fromTime: date };
  };

  const setSelectedToTime = (dbType: DBType, date?: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedTimeData.value[dbType] = { ...selectedTimeData.value[dbType], toTime: date };
  };

  const setSelectedFromUtc0Time = (dbType: DBType, date?: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedTimeData.value[dbType] = { ...selectedTimeData.value[dbType], fromUtc0Time: date };
  };

  const setSelectedToUtc0Time = (dbType: DBType, date?: string) => {
    if (isNil(dbType)) {
      return;
    }

    selectedTimeData.value[dbType] = { ...selectedTimeData.value[dbType], toUtc0Time: date };
  };

  const initSelectedTime = (dbType: DBType) => {
    if (isNil(dbType)) {
      return;
    }

    selectedTimeData.value[dbType] = { ...INITIAL_TIME_DATA };
  };

  const initAllSelectedTime = () => {
    selectedTimeData.value = getInitialSourceMap<StoredTimeData>({ ...INITIAL_TIME_DATA });
  };

  return {
    getSelectedFromTime,
    getSelectedToTime,
    getSelectedFromUtc0Time,
    getSelectedToUtc0Time,

    setSelectedFromTime,
    setSelectedToTime,
    setSelectedFromUtc0Time,
    setSelectedToUtc0Time,

    initSelectedTime,
    initAllSelectedTime,
  };
};

export const useDatabasePaConditionStore = defineStore('databasePaCondition', () => {
  const { getInstancesByDbType, getInstanceGroupsByDbType } = useDatabaseMetaStore();

  const {
    getSelectedGroupId,
    getSelectedInstanceId,
    getSelectedInstanceName,

    setSelectedGroupId,
    setSelectedInstanceId,
    setSelectedInstanceName,

    initSelectedInstance,
    initAllSelectedInstance,
  } = useDatabasePaInstanceCondition();

  const {
    getSelectedFromTime,
    getSelectedToTime,
    getSelectedFromUtc0Time,
    getSelectedToUtc0Time,

    setSelectedFromTime,
    setSelectedToTime,
    setSelectedFromUtc0Time,
    setSelectedToUtc0Time,

    initSelectedTime,
    initAllSelectedTime,
  } = useDatabasePaTimeCondition();

  const getAllInstanceGroups = (dbType: DBType) => {
    if (isNil(dbType)) {
      return [];
    }

    return getInstanceGroupsByDbType(dbType);
  };

  const getAllInstances = (dbType: DBType): InstanceInfo[] => {
    if (isNil(dbType)) {
      return [];
    }

    if (dbType === DB_TYPE.SINGLESTORE) {
      return getInstancesByDbType(dbType).filter(nodeIdFilterCallback);
    }

    return getInstancesByDbType(dbType);
  };

  const getInstancesByGroupId = (dbType: DBType, folderId?: string | null): InstanceInfo[] => {
    if (isNil(dbType)) {
      return [];
    }

    return getAllInstances(dbType).filter(({ folders }) =>
      folders?.some((folder) => folder.folderId === folderId),
    );
  };

  const getInstanceById = (dbType: DBType, instanceId: string): InstanceInfo | null => {
    if (isNil(dbType)) {
      return null;
    }

    return getAllInstances(dbType).find((instance) => instance.instanceId === instanceId) ?? null;
  };

  const getInstanceNameById = (dbType: DBType, instanceId: string): string => {
    if (isNil(dbType)) {
      return '';
    }

    return getInstanceById(dbType, instanceId)?.name ?? '';
  };

  const getNodeIdsByInstanceIdSinglestore = (dbType: DBType, instanceId: string): string[] => {
    if (dbType !== DB_TYPE.SINGLESTORE) {
      return [];
    }

    return (
      getInstancesByDbType(dbType)
        .filter((instance) => instance.instanceId?.startsWith(`${instanceId}|`))
        ?.map<string>((instance) => instance.instanceId!) || []
    );
  };

  const setData = (dbType: DBType) => (data: StoredData) => {
    Object.keys(data).forEach((key) => {
      switch (key) {
        case 'groupId':
          setSelectedGroupId(dbType, data[key] ?? '');
          break;
        case 'instanceId':
          setSelectedInstanceId(dbType, data[key] ?? '');
          break;
        case 'instanceName':
          if (!data[key] && data.instanceId) {
            data[key] = getInstanceNameById(dbType, data.instanceId);
          } else {
            setSelectedInstanceName(dbType, data[key]);
          }
          break;
        case 'fromTime':
          setSelectedFromTime(dbType, data[key]);
          break;
        case 'toTime':
          setSelectedToTime(dbType, data[key]);
          break;
        case 'fromUtc0Time':
          setSelectedFromUtc0Time(dbType, data[key]);
          break;
        case 'toUtc0Time':
          setSelectedToUtc0Time(dbType, data[key]);
          break;
        default:
          break;
      }
    });
  };

  const setPaConditionData = (dbType: DBType) => {
    return (data: StoredData) => setData(dbType)(data);
  };

  const setMysqlSetData = setData('mysql');
  const setOracleSetData = setData('oracle');
  const setSqlServerSetData = setData('sqlserver');
  const setPostgresqlSetData = setData('postgresql');
  const setAltibaseSetData = setData('altibase');
  const setTiberoSetData = setData('tibero');
  const setClickhouseSetData = setData('clickhouse');
  const setRedshiftSetData = setData('redshift');
  const setSinglestoreSetData = setData('singlestore');

  const initSelectedInfo = (dbType: DBType) => {
    if (isNil(dbType)) {
      return;
    }

    initSelectedInstance(dbType);
    initSelectedTime(dbType);
  };

  const initAllSelectedInfo = () => {
    initAllSelectedInstance();
    initAllSelectedTime();
  };

  return {
    getSelectedGroupId,
    getSelectedInstanceId,
    getSelectedInstanceName,
    getSelectedFromTime,
    getSelectedToTime,
    getSelectedFromUtc0Time,
    getSelectedToUtc0Time,

    setSelectedInstanceId,
    setSelectedInstanceName,
    setSelectedFromTime,
    setSelectedToTime,
    setSelectedFromUtc0Time,
    setSelectedToUtc0Time,

    setPaConditionData,

    setMysqlSetData,
    setOracleSetData,
    setSqlServerSetData,
    setPostgresqlSetData,
    setAltibaseSetData,
    setTiberoSetData,
    setClickhouseSetData,
    setRedshiftSetData,
    setSinglestoreSetData,

    initSelectedInfo,
    initAllSelectedInfo,

    getAllInstanceGroups,
    getAllInstances,
    getInstancesByGroupId,
    getInstanceById,
    getInstanceNameById,

    getNodeIdsByInstanceIdSinglestore,
  };
});
